// Alarms — Settings safety-net view.
//
// Lets the user see every alarm that's currently scheduled (one-shot
// timers + daily recurring) and kill any of them. Creation is NOT in
// this surface — that lives in the chat (set_alarm tool / /remind-me
// skill) or the Android native numpad. This page is purely "I want to
// verify what's set, and turn off the one that's haywire."
//
// Live updates: subscribes to the SSE channel for alarm:schedule /
// alarm:cancel events emitted by the server-side RPCs so the list
// stays in sync across tabs and after agent-driven scheduling.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type Alarm } from "../../lib/rpc";
import { sse } from "../../lib/sse";
import { confirm } from "../../components/confirm";

interface SettingsAlarmsProps {
  notify: (msg: string) => void;
}

export function SettingsAlarms({ notify }: SettingsAlarmsProps) {
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  // Initial load + SSE-driven refresh. The server emits alarm:schedule /
  // alarm:cancel events on alarms.set / alarms.cancel; an agent-issued
  // set_alarm fires through the same path so this listing reflects
  // chat-driven creates without manual refresh.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      api.alarmsList()
        .then((list) => { if (!cancelled) setAlarms(list); })
        .catch(() => { if (!cancelled) setAlarms([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    refresh();
    const unsub = sse.on((ev: any) => {
      if (ev.type === "alarm:schedule" || ev.type === "alarm:cancel") refresh();
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  // Tick once a minute so "fires in 3m" labels stay accurate without a
  // hot loop. The label resolution is minute-grain so a 60s cadence is
  // enough; a 1s cadence would just churn React re-renders for nothing.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const onCancel = async (a: Alarm) => {
    const label = a.kind === "daily" ? `daily ${a.atHHMM} "${a.name}"` : `"${a.name}"`;
    const ok = await confirm({
      title: "Cancel alarm?",
      message: `Stop ${label}. This can't be undone — you'd need to re-create it.`,
      confirmLabel: "Cancel alarm",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.alarmsCancel(a.id);
      // Optimistic local remove — the SSE refresh will reconcile.
      setAlarms((prev) => prev.filter((x) => x.id !== a.id));
      notify("Cancelled");
    } catch (err: any) {
      notify(err?.message || "cancel failed");
    }
  };

  const timers = alarms.filter((a) => a.kind === "once").sort((a, b) => a.fireAt - b.fireAt);
  const daily = alarms.filter((a) => a.kind === "daily").sort((a, b) =>
    (a.atHHMM || "").localeCompare(b.atHHMM || "")
  );

  return (
    <div className="settings-section">
      <h3 className="settings-section-title">Alarms</h3>
      <p className="settings-section-sub">
        Everything currently scheduled. Use Cancel to kill an alarm that's
        firing when it shouldn't. New alarms get set through chat
        ("set a 15-minute pasta timer") or on Android.
      </p>

      <div className="alarms-group">
        <div className="alarms-group-head">
          <h4 className="alarms-group-title">
            <Icon name="clock" size={13} /> Timers
            <span className="alarms-group-count">{timers.length}</span>
          </h4>
        </div>
        {loading ? (
          <p className="alarms-empty">loading…</p>
        ) : timers.length === 0 ? (
          <p className="alarms-empty">No timers set.</p>
        ) : (
          <ul className="alarms-list">
            {timers.map((a) => (
              <AlarmRow key={a.id} alarm={a} now={now} onCancel={() => onCancel(a)} />
            ))}
          </ul>
        )}
      </div>

      <div className="alarms-group">
        <div className="alarms-group-head">
          <h4 className="alarms-group-title">
            <Icon name="alarm" size={13} /> Recurring
            <span className="alarms-group-count">{daily.length}</span>
          </h4>
        </div>
        {loading ? (
          <p className="alarms-empty">loading…</p>
        ) : daily.length === 0 ? (
          <p className="alarms-empty">No recurring alarms set.</p>
        ) : (
          <ul className="alarms-list">
            {daily.map((a) => (
              <AlarmRow key={a.id} alarm={a} now={now} onCancel={() => onCancel(a)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AlarmRow({ alarm, now, onCancel }: { alarm: Alarm; now: number; onCancel: () => void }) {
  const when = alarm.kind === "daily"
    ? `Daily at ${formatHHMM(alarm.atHHMM)}`
    : formatRelativeOrAbsolute(alarm.fireAt, now);
  return (
    <li className="alarms-row">
      <div className="alarms-row-main">
        <div className="alarms-row-name">{alarm.name || "(unnamed)"}</div>
        <div className="alarms-row-when">{when}</div>
        {alarm.body && <div className="alarms-row-body">{alarm.body}</div>}
      </div>
      <button
        className="btn-secondary alarms-row-cancel"
        onClick={onCancel}
        type="button"
        title="Cancel alarm"
      >
        <Icon name="x" size={12} /> Cancel
      </button>
    </li>
  );
}

// Reformat HH:MM into a 12-hour-clock human label for the recurring
// list. Server stores 24h; users mostly think in 12h on a household
// device. Falls back to the raw HH:MM if parsing fails.
function formatHHMM(hhmm: string | null): string {
  if (!hhmm) return "(unknown)";
  const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const min = m[2];
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${min} ${period}`;
}

// "Fires in 12m" for soon; switch to a local date/time string once the
// alarm is more than a day out (relative phrasing gets weird past ~24h).
function formatRelativeOrAbsolute(fireAtMs: number, nowMs: number): string {
  const delta = fireAtMs - nowMs;
  if (delta <= 0) return "Firing now";
  const totalSec = Math.round(delta / 1000);
  if (totalSec < 60) return `in ${totalSec}s`;
  const totalMin = Math.round(totalSec / 60);
  if (totalMin < 60) return `in ${totalMin}m`;
  const totalHr = Math.round(totalMin / 60);
  if (totalHr < 24) {
    const remMin = totalMin % 60;
    return remMin > 0 ? `in ${totalHr}h ${remMin}m` : `in ${totalHr}h`;
  }
  // > 24h: absolute date/time in user's locale.
  return new Date(fireAtMs).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}
